import React, { useState } from "react";
import api from "../../api";

const CommentList = ({ comments, setComments, currentUser }) => {
  const [editingId, setEditingId] = useState(null);
  const [editContent, setEditContent] = useState("");
  const [loadingId, setLoadingId] = useState(null);
  const [error, setError] = useState("");

  const isOwner = (comment) =>
    currentUser && comment.userId && Number(currentUser.id) === Number(comment.userId);

  const handleEditStart = (comment) => {
    setEditingId(comment.id);
    setEditContent(comment.content);
    setError("");
  };

  const handleEditCancel = () => {
    setEditingId(null);
    setEditContent("");
    setError("");
  };

  const handleEditSubmit = async (id) => {
    if (!editContent.trim()) return;

    try {
      setLoadingId(id);
      setError("");

      await api.put(`/comments/${id}`, { content: editContent.trim() });

      setComments((prev) =>
        prev.map((c) => c.id === id ? { ...c, content: editContent.trim() } : c)
      );
      setEditingId(null);
    } catch (err) {
      setError(err.response?.data?.message || "Erreur lors de la modification.");
    } finally {
      setLoadingId(null);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Supprimer ce commentaire ?")) return;

    try {
      setLoadingId(id);
      setError("");

      await api.delete(`/comments/${id}`);

      setComments((prev) => prev.filter((c) => c.id !== id));
    } catch (err) {
      setError(err.response?.data?.message || "Erreur lors de la suppression.");
    } finally {
      setLoadingId(null);
    }
  };

  return (
    <div className="mt-6">
      <h3 className="text-xl font-bold text-white mb-6">Commentaires récents</h3>

      {error && (
        <div className="mb-4 bg-red-100 border border-red-300 text-red-700 px-4 py-3 rounded text-sm">
          {error}
        </div>
      )}

      {comments.length > 0 ? (
        comments.map((comment) => (
          <div key={comment.id} className="bg-gray-900 p-4 rounded-xl shadow mb-4 flex gap-4 items-start">
            <div className="flex-shrink-0">
              {comment.avatar ? (
                <img
                  src={`${process.env.REACT_APP_IMG_URL}/${comment.avatar}`}
                  alt="Avatar"
                  className="w-12 h-12 rounded-full object-cover border border-gray-700"
                />
              ) : (
                <div className="w-12 h-12 rounded-full bg-gray-700 flex items-center justify-center text-gray-400 text-xs border border-gray-600">
                  ?
                </div>
              )}
            </div>

            <div className="flex-1">
              <div className="mb-1 text-gray-400 text-sm">
                <span className="font-bold text-orange-500">
                  {comment.author || "Utilisateur"}
                </span>{" "}
                dit :
              </div>

              {editingId === comment.id ? (
                <div className="mt-2">
                  <textarea
                    rows="3"
                    value={editContent}
                    onChange={(e) => setEditContent(e.target.value)}
                    className="w-full py-2 px-3 rounded border border-gray-600 bg-gray-800 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-orange-500 resize-none text-sm"
                  />
                  <div className="flex gap-2 mt-2">
                    <button
                      onClick={() => handleEditSubmit(comment.id)}
                      disabled={loadingId === comment.id || !editContent.trim()}
                      className="bg-orange-500 hover:bg-orange-700 disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-bold py-1 px-3 rounded transition"
                    >
                      {loadingId === comment.id ? "Envoi..." : "Sauvegarder"}
                    </button>
                    <button
                      onClick={handleEditCancel}
                      className="bg-gray-600 hover:bg-gray-700 text-white text-sm font-bold py-1 px-3 rounded transition"
                    >
                      Annuler
                    </button>
                  </div>
                </div>
              ) : (
                <p className="text-gray-300 mb-3">{comment.content}</p>
              )}

              {comment.createdAt && (
                <p className="text-xs text-gray-500 mb-3">
                  {new Date(comment.createdAt).toLocaleDateString("fr-BE")}
                </p>
              )}

              {isOwner(comment) && editingId !== comment.id && (
                <div className="flex gap-2">
                  <button
                    onClick={() => handleEditStart(comment)}
                    className="bg-blue-500 hover:bg-blue-700 text-white text-sm font-bold py-1 px-3 rounded transition"
                  >
                    Modifier
                  </button>
                  <button
                    onClick={() => handleDelete(comment.id)}
                    disabled={loadingId === comment.id}
                    className="bg-red-500 hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-bold py-1 px-3 rounded transition"
                  >
                    {loadingId === comment.id ? "Suppression..." : "Supprimer"}
                  </button>
                </div>
              )}
            </div>
          </div>
        ))
      ) : (
        <p className="text-gray-400">Aucun commentaire pour le moment.</p>
      )}
    </div>
  );
};

export default CommentList;